import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as S from './styles';
import { InputGroup, IconButton, Whisper, Tooltip } from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import ArrowUpLineIcon from '@rsuite/icons/ArrowUpLine';
import ArrowDownLineIcon from '@rsuite/icons/ArrowDownLine';
import { FaPen, FaPlus, FaLink, FaTrash } from 'react-icons/fa';
import { CreatEdit } from './CreateEdit';
import { CreateEditGroupAditionals } from './CreateEditGroup';
import { CustomInput } from '../CustomInput';
import { CustomButton } from '../CustomButton';
import { useCustomTheme } from '../../hooks/useCustomTheme';
import { formatCurrencyBRL } from '../../utils/utils';
import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import { toastSuccess, toastError } from '../../toasts';
import { useAlert } from '../../contexts/Alert.context';
import { getSelectedAdditionalsGroupsByProduct } from '../../services/product_additionals/getSelectedAdditionalsGroupByProduct';
import { getSelectedAdditionalsGroupsByGroup } from '../../services/group_additionals/getSelectedAdditionalsGroupByGroup';
import { createSelectedAdditionalsGroupsByGroup } from '../../services/group_additionals/createSelectedAdditionalsGroupByGroup';
import { CreateGrupoAdicionais } from '../../entities/GrupoAdicionais';
import { deleteSelectedAdditionalsGroupsByGroup } from '../../services/group_additionals/deleteSelectedAdditionalsGroupByGroup';
import { LinkAdditionals } from './Link';
import { CustomCheckBox } from '../CustomCheckBox';

export function Additionals({ product, group: editGroup }) {
  const { id_empresa } = useAuth();
  const { theme } = useCustomTheme();
  const { showAlert } = useAlert();
  const [activeGroupId, setActiveGroupId] = useState(0);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [currentGroup, setCurrentGroup] = useState({});
  const [currentAdditional, setCurrentAdditional] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [openGroupModal, setOpenGroupModal] = useState(false);
  const [openLinkModal, setOpenLinkModal] = useState(false);
  const [additionalGroups, setAdditionalGroups] = useState([]);
  const [additionals, setAdditionals] = useState([]);
  const [searchText, setSearchText] = useState('');

  const handleIsSelected = (group) => {
    return !!selectedGroups.find((g) => g.IdGrupo === group.Id && group.Ativo);
  };

  const orderedBySelected = additionalGroups.reduce(
    (acc, curr) =>
      handleIsSelected(curr)
        ? (acc = [curr, ...acc])
        : (acc = [...acc, curr]),
    [],
  );

  const filteredGroups = useMemo(
    () =>
      searchText
        ? orderedBySelected.filter((group) =>
            String(group.Descricao)
              .toLocaleLowerCase()
              .includes(searchText.toLocaleLowerCase()),
          )
        : orderedBySelected,
    [searchText, orderedBySelected],
  );

  const getAdditionalGroups = useCallback(async () => {
    try {
      const response = await api.get(
        `/grupo-produto/listarAdicionaisTodos/${id_empresa}`,
      );
      setAdditionalGroups(response.data);
    } catch (error) {
      console.log(error);
    }
  }, [id_empresa]);

  const getSelectedByProduct = useCallback(async () => {
    if (product && product?.Id) {
      const data = await getSelectedAdditionalsGroupsByProduct(product.Id);
      setSelectedGroups(data);
    }
  }, [product, id_empresa]);

  const getSelectedByGroup = useCallback(async () => {
    if (editGroup && editGroup?.Id) {
      const data = await getSelectedAdditionalsGroupsByGroup(
        id_empresa,
        editGroup.Id,
      );
      setSelectedGroups(data);
    }
  }, [editGroup, id_empresa]);

  const handleOpenGroup = (group) => {
    if (group.Id === activeGroupId) {
      setActiveGroupId(0);
      return;
    }

    setAdditionals([]);
    getAdditionals(group.Id);
    setActiveGroupId(group.Id);
  };

  const handleOpenLinkModal = async (group) => {
    await getAdditionals(group.Id);
    setCurrentGroup(group);
    setOpenLinkModal(true);
  };

  async function getAdditionals(groupId) {
    try {
      const response = await api.get(
        `/produto/listarAdicionaisPorGrupo/${groupId}`,
      );
      setAdditionals(response.data);
    } catch (err) {
      console.log(err);
    }
  }

  async function createSelectionGroup(groupId) {
    const response = await createSelectedAdditionalsGroupsByGroup(
      new CreateGrupoAdicionais({
        IdGrupoSelecao: editGroup.Id,
        IdGrupo: groupId,
        IdEmpresa: id_empresa,
      }),
    );

    if (!response.success) {
      return toastError('Não foi possivel efetuar alteração');
    }

    toastSuccess('Alteração efetuada com sucesso');
    getSelectedByGroup();
  }

  async function createSelectionProduct(groupId) {
    try {
      await api.post(`/produto-adicionais/criar`, {
        IdProduto: product?.Id,
        IdGrupo: groupId,
      });

      toastSuccess('Alteração efetuada com sucesso');

      if (product) getSelectedByProduct();
    } catch (error) {
      toastError('Não foi possivel efetuar alteração');
    }
  }

  async function deleteSelectionGroup(selected) {
    const response = await deleteSelectedAdditionalsGroupsByGroup(selected.Id);

    if (!response.success) {
      return toastError('Não foi possivel efetuar alteração');
    }

    toastSuccess('Alteração efetuada com sucesso');

    getSelectedByGroup();
  }

  async function deleteSelectionProduct(groupId, selected) {
    try {
      await api.delete(`/produto-adicionais/remover/${selected.Id}`);

      toastSuccess('Alteração efetuada com sucesso');
      setSelectedGroups((prev) => prev.filter((g) => g.IdGrupo !== groupId));
    } catch (error) {
      toastError('Não foi possivel efetuar alteração');
    }
  }

  async function handleSelectGroup(group) {
    if(!group.Ativo) {
      toastError('Não é possivel selecionar um grupo inativo');
      return;
    }

    const selected = selectedGroups.find((g) => g.IdGrupo === group.Id);

    if (selected) {
      if (product) deleteSelectionProduct(group.Id, selected);
      if (editGroup) deleteSelectionGroup(selected);
      return;
    }

    if (product) createSelectionProduct(group.Id);
    if (editGroup) createSelectionGroup(group.Id);
  }

  const handleOpenModal = ({ group, resetAditional = true }) => {
    if (resetAditional) {
      setCurrentAdditional(null);
    }

    setOpenModal(true);
    setCurrentGroup(group);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleOpenGroupModal = (resetCurrent = true) => {
    if (resetCurrent) {
      setCurrentGroup({});
    }
    setOpenGroupModal((prev) => !prev);
  };

  function handleOpenAdditional(current, group) {
    setCurrentAdditional(current);
    handleOpenModal({ group, resetAditional: false });
  }

  function handleAddAdditional(additional) {
    if (!additional?.Id) {
      return;
    }
    setAdditionals((prev) => [...prev, additional]);
  }

  function handleEditAdditional(additional) {
    if (!additional?.Id) {
      return;
    }
    setAdditionals((prev) =>
      prev.map((add) => (add.Id !== additional.Id ? add : additional)),
    );
  }

  function handleRemoveAdditional(additionalId) {
    if (!additionalId) {
      return;
    }

    setAdditionals((prev) => prev.filter((add) => add.Id !== additionalId));
  }

  async function deleteAdditional(additional) {
    try {
      await api.delete(`/produto/excluir/${additional.Id}`);
      toastSuccess('Adicional excluído com sucesso');
      handleRemoveAdditional(additional.Id);
    } catch (error) {
      toastError('Não foi possivel excluir o adicional');
    }
  }

  function handleDeleteAdditional(additional) {
    showAlert({
      title: `Excluir ${additional?.Descricao}`,
      text: 'Deseja realmente excluir esse item? Essa ação não pode ser desfeita.',
      onSubmit: () => deleteAdditional(additional),
      submitText: 'Excluir',
    });
  }

  function handleEditGroup(group) {
    setCurrentGroup(group);
    handleOpenGroupModal(false);
  }

  async function handleToggleGroupActive(group) {
    try {
      const response = await api.patch(`/grupo-produto/alterar/${group.Id}`, {
        Ativo: !group.Ativo,
      });

      handleEditAdditionalGroup(response.data);
      toastSuccess(
        response.data.Ativo
          ? 'Grupo ativado com sucesso'
          : 'Grupo desativado com sucesso',
      );
    } catch (error) {
      toastError('Não foi possivel alterar o status do grupo');
    }
  }

  async function deleteAdditionalGroup(group) {
    try {
      await api.delete(`/grupo-produto/${group.Id}`);

      toastSuccess('Grupo de adicionais excluído com sucesso');
      setAdditionalGroups((prev) => prev.filter((g) => g.Id !== group.Id));

      if (activeGroupId === group.Id) {
        setActiveGroupId(0);
      }
    } catch (error) {
      toastError('Não foi possivel excluir o grupo de adicionais');
    }
  }

  function handleDeleteGroup(group) {
    showAlert({
      title: `Excluir ${group?.Descricao}`,
      text: 'Deseja realmente excluir esse grupo de adicionais? Essa ação não pode ser desfeita.',
      onSubmit: () => deleteAdditionalGroup(group),
      submitText: 'Excluir',
    });
  }

  function handleAddAdditionalGroup(newGroup) {
    if (!newGroup?.Id) {
      return;
    }

    setAdditionalGroups((prev) => [...prev, newGroup]);
  }

  function handleEditAdditionalGroup(editedGroup) {
    if (!editedGroup?.Id) {
      return;
    }

    setAdditionalGroups((prev) =>
      prev.map((group) => (group?.Id !== editedGroup.Id ? group : editedGroup)),
    );
  }

  function handleChangeSearch(text) {
    setSearchText(text);
  }

  useEffect(() => {
    getAdditionalGroups();
  }, [getAdditionalGroups]);

  useEffect(() => {
    if (product) getSelectedByProduct();
  }, [getSelectedByProduct, product]);

  useEffect(() => {
    if (editGroup) getSelectedByGroup();
  }, [getSelectedByGroup, editGroup]);

  return (
    <S.Container>
      <S.Header>
        <InputGroup style={{ maxWidth: '320px' }}>
          <CustomInput
            placeholder="Pesquisar grupo de adicionais..."
            onChange={handleChangeSearch}
            value={searchText}
          />
          <InputGroup.Addon>
            <SearchIcon color={theme.colors.textSecondary} />
          </InputGroup.Addon>
        </InputGroup>
        <CustomButton onClick={handleOpenGroupModal}>Cadastrar Grupo de Adicionais</CustomButton>
      </S.Header>

      <S.Content>
        {filteredGroups.map((group) => (
          <S.AdditionalsGroupCard
            key={group?.Id}
            selected={handleIsSelected(group)}
          >
            <S.AdditionalGroupCardHeader>
              <CustomCheckBox
                onClick={() => handleSelectGroup(group)}
                checked={handleIsSelected(group)}
              />
              <S.AdditionalGroupTitle
                onClick={() => handleOpenGroup(group)}
                selected={handleIsSelected(group)}
              >
                {activeGroupId === group.Id && additionals.length > 0 ?
                  <ArrowUpLineIcon style={{ fontSize: 26 }} /> :
                  <ArrowDownLineIcon style={{ fontSize: 26 }}/>
                } {group?.Descricao}
              </S.AdditionalGroupTitle>
              <Whisper placement="top" trigger="hover" speaker={<Tooltip>{group?.Ativo ? 'Clique para desativar' : 'Clique para ativar'}</Tooltip>}>
                <S.AdditionalGroupStatus
                  active={group?.Ativo}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleGroupActive(group);
                  }}
                >
                  {group?.Ativo ? 'Ativo' : 'Inativo'}
                </S.AdditionalGroupStatus>
              </Whisper>
              <S.HeaderActions>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Vincular item existente</Tooltip>}>
                  <IconButton
                    size="sm"
                    appearance="subtle"
                    icon={<FaLink size={13} />}
                    onClick={() => handleOpenLinkModal(group)}
                  />
                </Whisper>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Adicionar item novo</Tooltip>}>
                  <IconButton
                    size="sm"
                    appearance="subtle"
                    icon={<FaPlus size={13} />}
                    onClick={() => handleOpenModal({ group })}
                  />
                </Whisper>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Editar grupo</Tooltip>}>
                  <IconButton
                    size="sm"
                    appearance="subtle"
                    icon={<FaPen size={12} />}
                    onClick={() => handleEditGroup(group)}
                  />
                </Whisper>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Excluir grupo</Tooltip>}>
                  <IconButton
                    size="sm"
                    appearance="subtle"
                    color="red"
                    icon={<FaTrash size={12} />}
                    onClick={() => handleDeleteGroup(group)}
                  />
                </Whisper>
              </S.HeaderActions>
            </S.AdditionalGroupCardHeader>
            <S.AdditionalGroupCardContent
              expanded={activeGroupId === group.Id && additionals.length > 0}
            >
              {activeGroupId === group.Id &&
                additionals?.map((adicional) => (
                  <S.AdditionalRow key={adicional.Id}>
                    <S.AdditionalInfo
                      onClick={() => handleOpenAdditional(adicional, group)}
                    >
                      <S.AdditionalTitle>
                        {adicional?.Descricao}
                      </S.AdditionalTitle>
                      <S.AdditionalPrice>
                        {formatCurrencyBRL(adicional?.PrecoVenda)}
                      </S.AdditionalPrice>
                    </S.AdditionalInfo>
                    <S.AdditionalRowActions>
                      <Whisper placement="top" trigger="hover" speaker={<Tooltip>Editar</Tooltip>}>
                        <IconButton
                          size="xs"
                          appearance="subtle"
                          icon={<FaPen size={11} />}
                          onClick={() => handleOpenAdditional(adicional, group)}
                        />
                      </Whisper>
                      <Whisper placement="top" trigger="hover" speaker={<Tooltip>Excluir</Tooltip>}>
                        <IconButton
                          size="xs"
                          appearance="subtle"
                          color="red"
                          icon={<FaTrash size={11} />}
                          onClick={() => handleDeleteAdditional(adicional)}
                        />
                      </Whisper>
                    </S.AdditionalRowActions>
                  </S.AdditionalRow>
                ))}
            </S.AdditionalGroupCardContent>
          </S.AdditionalsGroupCard>
        ))}
      </S.Content>
      {openModal && <CreatEdit
        open={openModal}
        close={handleCloseModal}
        currentGroupId={currentGroup?.Id}
        currentAdditional={currentAdditional}
        handleAddAdditional={handleAddAdditional}
        handleEditAdditional={handleEditAdditional}
        handleRemoveAdditional={handleRemoveAdditional}
        getAdditionals={getAdditionals}
        setCurrentAdditional={setCurrentAdditional}
      />}
      {openGroupModal && <CreateEditGroupAditionals
        open={openGroupModal}
        close={handleOpenGroupModal}
        currentGroup={currentGroup}
        handleAddAdditionalGroup={handleAddAdditionalGroup}
        handleEditAdditionalGroup={handleEditAdditionalGroup}
      />}
      {openLinkModal && <LinkAdditionals
        open={openLinkModal}
        close={() => setOpenLinkModal(false)}
        additionalsGroup={currentGroup}
        additionalsSelected={additionals.filter((add) => add.Grupo === currentGroup.Id)}
        setAdditionals={setAdditionals}
      />}
    </S.Container>
  );
}
